import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FloppyDisk, Envelope, Copy, Eye } from "@phosphor-icons/react";

const DEFAULT_TEMPLATE = "Se ha asignado al perito {perito_name} al siniestro {numero_siniestro} para la modalidad {modalidad} con cita concertada para el día {fecha} entre las {hora_inicio} y las {hora_fin} horas. Tlf. Perito: {perito_telefono}. Email perito: {perito_email}";
const DEFAULT_SUBJECT = "Cita concertada · Siniestro {numero_siniestro}";

const TOKENS = [
  "perito_name", "perito_telefono", "perito_email",
  "numero_siniestro", "numero_siniestro_gabinete",
  "modalidad", "fecha", "hora_inicio", "hora_fin",
  "asegurado_nombre", "direccion_riesgo",
];

/**
 * Configuración de notificación de cita por compañía.
 *
 * Modos:
 *  - none  → el gestor no recibe nada (la cita queda sólo en la agenda)
 *  - copy  → el gestor copia un texto que pegar en la web del peritaje
 *  - email → la app envía un email automático a `cita_notification_email_to`
 */
export default function CitaNotificationTab({ aseguradoraId, canEdit }) {
  const [form, setForm] = useState({
    cita_notification_mode: "none",
    cita_notification_email_to: "",
    cita_notification_email_cc: "",
    cita_notification_email_subject: "",
    cita_notification_template: "",
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/aseguradoras/${aseguradoraId}`)
      .then((r) => {
        const a = r.data || {};
        setForm({
          cita_notification_mode: a.cita_notification_mode || "none",
          cita_notification_email_to: a.cita_notification_email_to || "",
          cita_notification_email_cc: a.cita_notification_email_cc || "",
          cita_notification_email_subject: a.cita_notification_email_subject || "",
          cita_notification_template: a.cita_notification_template || "",
        });
      })
      .catch(() => toast.error("No se pudo cargar la aseguradora"))
      .finally(() => setLoading(false));
  }, [aseguradoraId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/aseguradoras/${aseguradoraId}`, form);
      toast.success("Notificación de cita guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const mode = form.cita_notification_mode;
  const preview = (form.cita_notification_template || DEFAULT_TEMPLATE)
    .replace("{perito_name}", "Manuel Holgado")
    .replace("{numero_siniestro}", "01202600239126")
    .replace("{numero_siniestro_gabinete}", "GAB-2026/0123")
    .replace("{modalidad}", "Videoperitación asegurado")
    .replace("{fecha}", "26/05/2026")
    .replace("{hora_inicio}", "10:00")
    .replace("{hora_fin}", "10:30")
    .replace("{perito_telefono}", "670336210")
    .replace("{perito_email}", "mholgado@dobilas.es")
    .replace("{asegurado_nombre}", "Juan Pérez García")
    .replace("{direccion_riesgo}", "Calle Mayor 12, Madrid");

  if (loading) return <div className="p-6 text-sm text-muted">Cargando...</div>;

  return (
    <div className="p-6 max-w-3xl space-y-6" data-testid="cita-notif-tab">
      <div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-orange-600">
          Cuando el gestor concierta una cita
        </div>
        <h2 className="text-lg font-semibold mt-0.5">Notificación a la aseguradora</h2>
        <p className="text-xs text-muted mt-1">
          Configura cómo se notifica la cita a esta compañía: un texto para copiar y pegar en su web, o un email automático a su buzón.
        </p>
      </div>

      {/* Modo */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { k: "none", label: "Ninguno", desc: "No mostrar nada", icon: Eye },
          { k: "copy", label: "Copiar texto", desc: "Para pegar en su web", icon: Copy },
          { k: "email", label: "Email automático", desc: "Enviar a su buzón", icon: Envelope },
        ].map((opt) => {
          const Icon = opt.icon;
          const active = mode === opt.k;
          return (
            <button
              key={opt.k}
              type="button"
              disabled={!canEdit}
              onClick={() => setForm((f) => ({ ...f, cita_notification_mode: opt.k }))}
              data-testid={`cita-mode-${opt.k}`}
              className={`border-2 rounded-lg p-4 text-left transition-all ${
                active
                  ? "border-orange-600 bg-orange-50 ring-2 ring-orange-600/20"
                  : "border-border bg-white hover:border-foreground"
              } disabled:opacity-50`}
            >
              <Icon className={`w-5 h-5 mb-2 ${active ? "text-orange-600" : "text-muted"}`} weight="bold" />
              <div className="text-sm font-bold">{opt.label}</div>
              <div className="text-[11px] text-muted mt-0.5">{opt.desc}</div>
            </button>
          );
        })}
      </div>

      {mode !== "none" && (
        <>
          {/* Email targets (solo modo email) */}
          {mode === "email" && (
            <div className="space-y-3 border-l-2 border-orange-600 pl-4">
              <Field
                label="Email destino *"
                value={form.cita_notification_email_to}
                onChange={(v) => setForm((f) => ({ ...f, cita_notification_email_to: v }))}
                placeholder="citas@aseguradora.com"
                type="email"
                disabled={!canEdit}
                testid="cita-email-to"
              />
              <Field
                label="CC (separar varios con coma)"
                value={form.cita_notification_email_cc}
                onChange={(v) => setForm((f) => ({ ...f, cita_notification_email_cc: v }))}
                placeholder="copia1@aseguradora.com, copia2@aseguradora.com"
                disabled={!canEdit}
                testid="cita-email-cc"
              />
              <Field
                label="Asunto del email"
                value={form.cita_notification_email_subject}
                onChange={(v) => setForm((f) => ({ ...f, cita_notification_email_subject: v }))}
                placeholder={DEFAULT_SUBJECT}
                disabled={!canEdit}
                testid="cita-email-subject"
              />
            </div>
          )}

          {/* Plantilla */}
          <div>
            <label className="text-[11px] font-semibold text-muted block mb-1.5">
              Plantilla de mensaje
            </label>
            <textarea
              rows={6}
              value={form.cita_notification_template}
              onChange={(e) => setForm((f) => ({ ...f, cita_notification_template: e.target.value }))}
              placeholder={DEFAULT_TEMPLATE}
              disabled={!canEdit}
              data-testid="cita-template"
              className="w-full text-sm font-mono border border-border rounded-md px-3 py-2 bg-white"
            />
            <div className="mt-2 flex flex-wrap gap-1">
              <span className="text-[10px] text-muted self-center">Tokens:</span>
              {TOKENS.map((t) => (
                <button
                  key={t}
                  type="button"
                  disabled={!canEdit}
                  onClick={() => setForm((f) => ({
                    ...f,
                    cita_notification_template: (f.cita_notification_template || "") + "{" + t + "}",
                  }))}
                  className="text-[10px] font-mono px-1.5 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded border border-slate-200"
                  data-testid={`token-${t}`}
                >
                  {`{${t}}`}
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="border border-emerald-300 bg-emerald-50 rounded-md p-3" data-testid="cita-preview">
            <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-800 mb-1.5">
              Vista previa con datos de ejemplo
            </div>
            <div className="text-[13px] text-emerald-900 whitespace-pre-wrap leading-relaxed">
              {preview}
            </div>
          </div>
        </>
      )}

      {canEdit && (
        <div className="pt-4 border-t border-border flex items-center gap-2">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="btn-brand text-sm px-4 py-2 rounded-md font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="cita-save"
          >
            <FloppyDisk className="w-4 h-4" weight="bold" />
            {saving ? "Guardando..." : "Guardar configuración"}
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", disabled, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold text-muted">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testid}
        className="mt-1 w-full text-sm border border-border rounded-md px-3 py-2 bg-white disabled:opacity-50"
      />
    </label>
  );
}
